/**
 * MenuItemFormBuilderModal — modal projektowania widoku FORMULARZA pozycji menu szablonu.
 *
 * Analogia do MenuItemViewBuilderModal (widok listy) — otwierany przez
 * TemplateMenuBuilderPage gdy super admin klika "Projektuj widok formularza" na pozycji
 * menu type='module'. Zapisuje wynik do node.view.formConfig w lokalnym stanie drzewa menu.
 *
 * Przepływ zapisu:
 *  1. Modal osadza SystemModuleBuilderPage z onSaveKartotekaLayout.
 *  2. Builder emituje layoutConfig = { elements: LayoutElement[] } (fieldId-based).
 *  3. handleBuilderSave tłumaczy elements → ModuleTemplateFormConfig (fieldId→fieldSlug).
 *  4. Wywołuje props.onSave(formConfig).
 *  5. Parent (TemplateMenuBuilderPage) robi updateInTree(node.id, n => ({...n, view:{...n.view, formConfig, v:1}})).
 *  6. isDirty flipuje się — istniejący "Zapisz menu" zapisuje całość do backendu.
 *
 * Tłumaczenie fieldId ↔ fieldSlug:
 *  - new_table: externalFields ma id===slug — identyczność, zero konwersji.
 *  - reference_to_system_entity: kartotekaFields mają id=UUID, slug=string —
 *    przy ładowaniu formConfig: fieldSlug→UUID (do initialLayoutConfig),
 *    przy zapisie elements: UUID→fieldSlug (do formConfig).
 *
 * Walker rekurencyjny (translateElements) schodzi po pełnym drzewie LayoutElement[]
 * (children / tabs[].children / accordionItems[].children / steps[].children)
 * zamiast spłaszczać je do sekcji (jak templateFormConfigToClientSections).
 * Builder systemowy oczekuje pełnego drzewa z fieldId.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type {
  MenuItem,
  ModuleTemplateEntityDefinition,
  ModuleTemplateFormConfig,
  ModuleTemplateViewDefinition,
} from '@saas/shared';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useKartotekaDefinition } from '@/lib/kartoteki/kartoteka-definition';
import { parseMenuViewRef } from '@/lib/module-templates/template-layouts';
import { cn } from '@/lib/utils';
import { SystemModuleBuilderPage } from '@/pages/system-modules/SystemModuleBuilderPage';

import { BuilderOverlay } from './BuilderOverlay';

// ---------------------------------------------------------------------------
// Stałe buildera (analogia do KARTOTEKA_READ_ONLY_TAB_TYPES w ModuleTemplateEditor)
// ---------------------------------------------------------------------------

/** Typy zakładek read-only w trybie super-admin form buildera (nie edytujemy ich zawartości). */
const FORM_READ_ONLY_TAB_TYPES: Array<
  'tree_picker' | 'satellite' | 'additional' | 'panel' | 'audit_log' | 'approvals'
> = ['tree_picker', 'satellite', 'additional', 'panel', 'audit_log', 'approvals'];

/** Kolekcje zakładek subcollection read-only (analogia ModuleTemplateEditor). */
const FORM_READ_ONLY_COLLECTIONS: string[] = ['contractor_company_access', 'user_company_access'];

// ---------------------------------------------------------------------------
// Typy (lokalne — nie importujemy wewnętrznych typów buildera)
// ---------------------------------------------------------------------------

/** Element LayoutElement z buildera — kształt wewnętrzny z fieldId. */
interface BuilderElement {
  id: string;
  type: string;
  fieldId?: string;
  settings?: Record<string, unknown>;
  style?: Record<string, unknown>;
  conditionalField?: string | null;
  conditionalOperator?: string | null;
  conditionalValue?: unknown;
  children?: BuilderElement[];
  tabs?: Array<{
    id: string;
    slug: string;
    title: string;
    icon?: string;
    validateOnChange?: boolean;
    blockOnError?: boolean;
    hidden?: boolean;
    sortOrder?: number;
    children: BuilderElement[];
  }>;
  accordionItems?: Array<{
    id: string;
    title: string;
    defaultExpanded?: boolean;
    sortOrder?: number;
    children: BuilderElement[];
  }>;
  steps?: Array<{
    id: string;
    title: string;
    icon?: string;
    validation?: boolean;
    sortOrder?: number;
    children: BuilderElement[];
  }>;
}

// ---------------------------------------------------------------------------
// Walker rekurencyjny — tłumaczenie LayoutElement[] ↔ ModuleTemplateFormElement[]
//
// Zachowuje pełne drzewo (tabs/children/accordionItems/steps).
// Dla elementów type!='field' przepisuje children rekurencyjnie.
// Dla elementów type='field' zamienia fieldId ↔ fieldSlug przez mapę.
// Dla new_table mapa identycznościowa → zero konwersji (id===slug).
// ---------------------------------------------------------------------------

/**
 * formConfig (fieldSlug) → BuilderElement[] (fieldId) — do initialLayoutConfig.
 * Pomija elementy type='field' bez mapowania w slugToId (defensywnie).
 */
function translateFormConfigToElements(
  elements: ModuleTemplateFormConfig['elements'],
  slugToId: ReadonlyMap<string, string>,
): BuilderElement[] {
  return elements.map((el): BuilderElement => {
    if (el.type === 'field') {
      const slug = el.fieldSlug ?? el.fieldId ?? '';
      const fieldId = slugToId.get(slug) ?? slug;
      return {
        id: el.id,
        type: el.type,
        fieldId,
        ...(el.settings ? { settings: el.settings } : {}),
        ...(el.style ? { style: el.style } : {}),
        ...(el.conditionalFieldSlug !== undefined
          ? { conditionalField: el.conditionalFieldSlug }
          : {}),
        ...(el.conditionalOperator !== undefined
          ? { conditionalOperator: el.conditionalOperator }
          : {}),
        ...(el.conditionalValue !== undefined ? { conditionalValue: el.conditionalValue } : {}),
      };
    }

    const out: BuilderElement = {
      id: el.id,
      type: el.type,
      ...(el.settings ? { settings: el.settings } : {}),
      ...(el.style ? { style: el.style } : {}),
      ...(el.conditionalFieldSlug !== undefined
        ? { conditionalField: el.conditionalFieldSlug }
        : {}),
      ...(el.conditionalOperator !== undefined
        ? { conditionalOperator: el.conditionalOperator }
        : {}),
      ...(el.conditionalValue !== undefined ? { conditionalValue: el.conditionalValue } : {}),
    };

    if (el.children?.length) {
      out.children = translateFormConfigToElements(el.children, slugToId);
    }
    if (el.tabs?.length) {
      out.tabs = el.tabs.map((tab) => ({
        id: tab.id,
        slug: tab.slug,
        title: tab.title,
        ...(tab.icon !== undefined ? { icon: tab.icon } : {}),
        ...(tab.validateOnChange !== undefined ? { validateOnChange: tab.validateOnChange } : {}),
        ...(tab.blockOnError !== undefined ? { blockOnError: tab.blockOnError } : {}),
        ...(tab.hidden !== undefined ? { hidden: tab.hidden } : {}),
        ...(tab.sortOrder !== undefined ? { sortOrder: tab.sortOrder } : {}),
        children: translateFormConfigToElements(tab.children, slugToId),
      }));
    }
    if (el.accordionItems?.length) {
      out.accordionItems = el.accordionItems.map((item) => ({
        id: item.id,
        title: item.title,
        ...(item.defaultExpanded !== undefined ? { defaultExpanded: item.defaultExpanded } : {}),
        ...(item.sortOrder !== undefined ? { sortOrder: item.sortOrder } : {}),
        children: translateFormConfigToElements(item.children, slugToId),
      }));
    }
    if (el.steps?.length) {
      out.steps = el.steps.map((step) => ({
        id: step.id,
        title: step.title,
        ...(step.icon !== undefined ? { icon: step.icon } : {}),
        ...(step.validation !== undefined ? { validation: step.validation } : {}),
        ...(step.sortOrder !== undefined ? { sortOrder: step.sortOrder } : {}),
        children: translateFormConfigToElements(step.children, slugToId),
      }));
    }

    return out;
  });
}

/**
 * BuilderElement[] (fieldId) → ModuleTemplateFormConfig (fieldSlug) — po zapisie z buildera.
 * Pomija type='field' bez mapowania w idToSlug (defensywnie zachowuje oryginalny fieldId).
 */
function translateElementsToFormConfig(
  elements: BuilderElement[],
  idToSlug: ReadonlyMap<string, string>,
): ModuleTemplateFormConfig['elements'] {
  return elements.map((el) => {
    if (el.type === 'field') {
      const slug = el.fieldId ? (idToSlug.get(el.fieldId) ?? el.fieldId) : '';
      return {
        id: el.id,
        type: el.type,
        fieldSlug: slug,
        ...(el.settings ? { settings: el.settings } : {}),
        ...(el.style ? { style: el.style } : {}),
        ...(el.conditionalField !== undefined ? { conditionalFieldSlug: el.conditionalField } : {}),
        ...(el.conditionalOperator !== undefined
          ? { conditionalOperator: el.conditionalOperator }
          : {}),
        ...(el.conditionalValue !== undefined ? { conditionalValue: el.conditionalValue } : {}),
      };
    }

    const out: ReturnType<typeof translateElementsToFormConfig>[number] = {
      id: el.id,
      type: el.type,
      ...(el.settings ? { settings: el.settings } : {}),
      ...(el.style ? { style: el.style } : {}),
      ...(el.conditionalField !== undefined ? { conditionalFieldSlug: el.conditionalField } : {}),
      ...(el.conditionalOperator !== undefined
        ? { conditionalOperator: el.conditionalOperator }
        : {}),
      ...(el.conditionalValue !== undefined ? { conditionalValue: el.conditionalValue } : {}),
    };

    if (el.children?.length) {
      out.children = translateElementsToFormConfig(el.children, idToSlug);
    }
    if (el.tabs?.length) {
      out.tabs = el.tabs.map((tab) => ({
        id: tab.id,
        slug: tab.slug,
        title: tab.title,
        ...(tab.icon !== undefined ? { icon: tab.icon } : {}),
        ...(tab.validateOnChange !== undefined ? { validateOnChange: tab.validateOnChange } : {}),
        ...(tab.blockOnError !== undefined ? { blockOnError: tab.blockOnError } : {}),
        ...(tab.hidden !== undefined ? { hidden: tab.hidden } : {}),
        ...(tab.sortOrder !== undefined ? { sortOrder: tab.sortOrder } : {}),
        children: translateElementsToFormConfig(tab.children, idToSlug),
      }));
    }
    if (el.accordionItems?.length) {
      out.accordionItems = el.accordionItems.map((item) => ({
        id: item.id,
        title: item.title,
        ...(item.defaultExpanded !== undefined ? { defaultExpanded: item.defaultExpanded } : {}),
        ...(item.sortOrder !== undefined ? { sortOrder: item.sortOrder } : {}),
        children: translateElementsToFormConfig(item.children, idToSlug),
      }));
    }
    if (el.steps?.length) {
      out.steps = el.steps.map((step) => ({
        id: step.id,
        title: step.title,
        ...(step.icon !== undefined ? { icon: step.icon } : {}),
        ...(step.validation !== undefined ? { validation: step.validation } : {}),
        ...(step.sortOrder !== undefined ? { sortOrder: step.sortOrder } : {}),
        children: translateElementsToFormConfig(step.children, idToSlug),
      }));
    }

    return out;
  });
}

// ---------------------------------------------------------------------------
// Props interfejsy
// ---------------------------------------------------------------------------

interface MenuItemFormBuilderModalProps {
  /** Pozycja menu, dla której projektujemy formularz. */
  node: MenuItem;
  /** Kod szablonu (slug). */
  templateCode: string;
  /**
   * Lista encji szablonu (lokalna kopia, mutowalnie). Każda encja ma `views`
   * z widokami `type==='form'` — dropdown layoutów czyta stąd.
   */
  entities: ModuleTemplateEntityDefinition[];
  /**
   * Wywołane gdy user zatwierdzi widok (kliknie "Zapisz" wewnątrz buildera).
   * Drugi argument `viewRef` ('entitySlug:viewSlug') ustawiany gdy user pracuje
   * z nazwanym layoutem. Gdy undefined — stary inline fallback.
   */
  onSave: (formConfig: ModuleTemplateFormConfig, viewRef?: string) => void;
  /** Zamknięcie bez zapisu. */
  onClose: () => void;
  /**
   * Tworzy nowy widok `form` dla encji o danym slugu, seedowany podanym formConfig.
   * Zwraca slug nowego widoku. Używane przez dropdown layoutów (+ / Nowy widok).
   */
  onCreateView: (
    entitySlug: string,
    name: string,
    seedFormConfig: ModuleTemplateFormConfig,
  ) => string;
  /**
   * Wymusza początkowo aktywny layout (slug; null = domyślny/wirtualny). Używane gdy
   * builder otwierany jest z modalu przypisania widoków „Przeglądaj/edytuj" dla
   * KONKRETNEGO layoutu — pomija wyliczenie z node.viewRef. `undefined` = stare
   * zachowanie (aktywny = z viewRef pozycji menu).
   */
  initialViewSlugOverride?: string | null;
  /**
   * Gdy true: builder otwiera się w trybie podglądu (read-only). Przycisk Zapisz
   * i przyciski resetu są ukryte. Canvas jest nieedytowalny (pointer-events: none).
   * Domyślnie false = zachowanie bez zmian (edytowalny).
   */
  readOnlyMode?: boolean;
}

// ---------------------------------------------------------------------------
// Komponent publiczny
// ---------------------------------------------------------------------------

export function MenuItemFormBuilderModal({
  node,
  templateCode,
  entities,
  onSave,
  onClose,
  onCreateView,
  initialViewSlugOverride,
  readOnlyMode = false,
}: MenuItemFormBuilderModalProps) {
  const { t } = useTranslation();

  // Wyznacz entitySlug z moduleCode: moduleCode = `${templateCode}_${entitySlug}`
  const entitySlug =
    node.moduleCode && node.moduleCode.startsWith(`${templateCode}_`)
      ? node.moduleCode.slice(templateCode.length + 1)
      : null;

  const entity = entitySlug ? entities.find((e) => e.slug === entitySlug) : null;

  const isReference =
    entity !== null && entity !== undefined && entity.ddlStrategy === 'reference_to_system_entity';
  const kartotekaSlug = entity?.referenceSystemModuleSlug ?? undefined;

  // Widoki `form` dla tej encji — źródło dropdownu layoutów.
  const formViews = entity ? entity.views.filter((v) => v.type === 'form') : [];

  // Wyznacz aktywny viewSlug z node.viewRef ('entitySlug:viewSlug') lub default.
  const activeViewSlugFromRef = (() => {
    if (!node.viewRef || !entitySlug) return null;
    const parsed = parseMenuViewRef(node.viewRef);
    return parsed?.entitySlug === entitySlug ? parsed.viewSlug : null;
  })();
  const defaultView = formViews.find((v) => v.isDefault) ?? formViews[0] ?? null;
  // Override (z modalu przypisania) ma priorytet nad viewRef pozycji menu.
  const initialActiveViewSlug =
    initialViewSlugOverride !== undefined
      ? initialViewSlugOverride
      : (activeViewSlugFromRef ?? defaultView?.slug ?? null);

  return (
    <MenuItemFormBuilderModalInner
      node={node}
      templateCode={templateCode}
      entity={entity ?? null}
      entitySlug={entitySlug}
      isReference={isReference}
      kartotekaSlug={kartotekaSlug}
      formViews={formViews}
      initialActiveViewSlug={initialActiveViewSlug}
      onSave={onSave}
      onClose={onClose}
      onCreateView={onCreateView}
      readOnlyMode={readOnlyMode}
      t={t}
    />
  );
}

// ---------------------------------------------------------------------------
// Inner — hooks zawsze wywoływane (reguła hooks)
// ---------------------------------------------------------------------------

function MenuItemFormBuilderModalInner({
  node,
  entity,
  entitySlug,
  isReference,
  kartotekaSlug,
  formViews,
  initialActiveViewSlug,
  onSave,
  onClose,
  onCreateView,
  readOnlyMode,
  t,
}: {
  node: MenuItem;
  templateCode: string;
  entity: ModuleTemplateEntityDefinition | null;
  entitySlug: string | null;
  isReference: boolean;
  kartotekaSlug: string | undefined;
  /** Widoki `form` dla tej encji — źródło dropdownu layoutów. */
  formViews: ModuleTemplateViewDefinition[];
  /** Slug aktywnego widoku (null = widok domyślny / wirtualny). */
  initialActiveViewSlug: string | null;
  onSave: (formConfig: ModuleTemplateFormConfig, viewRef?: string) => void;
  onClose: () => void;
  onCreateView: (
    entitySlug: string,
    name: string,
    seedFormConfig: ModuleTemplateFormConfig,
  ) => string;
  readOnlyMode: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const kartotekaQuery = useKartotekaDefinition(isReference ? kartotekaSlug : undefined);

  // --- Mapy pól ---
  // new_table: externalFields z id===slug → mapa identycznościowa
  // reference: kartotekaFields z id=UUID, slug=string → slug→UUID i UUID→slug
  const { slugToId, idToSlug, externalFields } = useMemo(() => {
    const kartotekaFields = kartotekaQuery.data?.fields ?? [];
    if (isReference) {
      const slugToId = new Map<string, string>(kartotekaFields.map((f) => [f.slug, f.id]));
      const idToSlug = new Map<string, string>(kartotekaFields.map((f) => [f.id, f.slug]));
      const externalFields = kartotekaFields.map((f) => ({
        id: f.id,
        slug: f.slug,
        name: f.name,
        fieldType: f.fieldType,
        isCore: f.isCore,
        isSystemLocked: false,
        isRequired: false,
        sortOrder: f.sortOrder ?? 0,
      }));
      return { slugToId, idToSlug, externalFields };
    }

    // new_table: id === slug — identyczność
    const fields = entity?.fields ?? [];
    const slugToId = new Map<string, string>(fields.map((f) => [f.slug, f.slug]));
    const idToSlug = new Map<string, string>(fields.map((f) => [f.slug, f.slug]));
    const externalFields = fields.map((f, i) => ({
      id: f.slug,
      slug: f.slug,
      name: f.name,
      fieldType: f.type,
      isCore: false,
      isSystemLocked: false,
      isRequired: f.isRequired ?? false,
      sortOrder: f.sortOrder ?? i,
    }));
    return { slugToId, idToSlug, externalFields };
  }, [isReference, kartotekaQuery.data, entity]);

  // ---------------------------------------------------------------------------
  // Stan aktywnego layoutu (dropdown)
  // ---------------------------------------------------------------------------

  /**
   * Slug aktywnego widoku. null = widok domyślny (wirtualny — brak nazwanego layoutu
   * albo wybrany isDefault). Gdy initialActiveViewSlug wskazuje istniejący widok,
   * inicjujemy od razu tym slugiem.
   */
  const [activeViewSlug, setActiveViewSlug] = useState<string | null>(initialActiveViewSlug);

  /** Modal „Nowy formularz" — widoczny gdy user klika + w dropdownie layoutów. */
  const [showNewViewModal, setShowNewViewModal] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [newViewNameError, setNewViewNameError] = useState('');

  /** Aktywny widok — szukamy po slugu w formViews (może być null gdy default / pusty). */
  const activeView = activeViewSlug ? formViews.find((v) => v.slug === activeViewSlug) : null;

  /** Czy aktywny layout jest domyślny (wirtualny lub z flagą isDefault). */
  const isActiveDefault = !activeViewSlug || (activeView?.isDefault ?? false);

  // ---------------------------------------------------------------------------
  // Seed initialLayoutConfig — dual-read (viewRef → entity.views, fallback inline)
  // ---------------------------------------------------------------------------

  /**
   * Buduje pełny domyślny layout dla new_table (wszystkie pola w jednej sekcji).
   * Wydzielona funkcja bo reużyta zarówno przy inicjalizacji jak i przy reset.
   */
  const buildDefaultLayout = useCallback((): Record<string, unknown> => {
    if (isReference && kartotekaQuery.data) {
      const defaultLayout = kartotekaQuery.data.layouts.find(
        (l) => l.layoutType === 'form' && l.isDefault,
      );
      if (defaultLayout?.layoutConfig && typeof defaultLayout.layoutConfig === 'object') {
        return defaultLayout.layoutConfig as Record<string, unknown>;
      }
    }
    if (!isReference && entity?.fields && entity.fields.length > 0) {
      const fieldElements = entity.fields.map((f, i) => ({
        id: `default-field-${i}`,
        type: 'field' as const,
        fieldId: f.slug, // new_table: id===slug
      }));
      return {
        elements: [
          {
            id: 'default-tabs-0',
            type: 'tabs' as const,
            tabs: [
              {
                id: 'default-tab-0',
                slug: 'data',
                title: t('admin.templateMenuBuilder.formBuilder.defaultTabTitle'),
                hidden: false,
                sortOrder: 0,
                children: [
                  {
                    id: 'default-section-0',
                    type: 'section' as const,
                    settings: { title: null, columns: 2 },
                    children: fieldElements,
                  },
                ],
              },
            ],
          },
        ],
      };
    }
    return { elements: [] };
  }, [isReference, kartotekaQuery.data, entity, t]);

  /**
   * Seed: formConfig (fieldSlug) → initialLayoutConfig (fieldId, drzewo).
   *
   * Priorytet (dual-read):
   *  1. activeView?.formConfig (widok named, z entity.views) — przetłumacz slug→fieldId.
   *  2. node.view?.formConfig (stary inline embed) — fallback dla istniejących szablonów.
   *  3. reference: domyślny layout z definicji kartoteki (fieldId=UUID, as-is).
   *  4. new_table: zbuduj pełny domyślny układ ze wszystkich pól.
   *  5. Pusty canvas.
   */
  const initialLayoutConfig = useMemo(() => {
    // 1. Aktywny named view (z entity.views)
    const namedFormConfig = activeView?.formConfig;
    if (
      namedFormConfig &&
      Array.isArray(namedFormConfig.elements) &&
      namedFormConfig.elements.length > 0
    ) {
      const translated = translateFormConfigToElements(namedFormConfig.elements, slugToId);
      return { elements: translated };
    }

    // 2. Stary inline fallback (zero regresji)
    const inlineFormConfig = node.view?.formConfig;
    if (
      !activeViewSlug &&
      inlineFormConfig &&
      Array.isArray(inlineFormConfig.elements) &&
      inlineFormConfig.elements.length > 0
    ) {
      const translated = translateFormConfigToElements(inlineFormConfig.elements, slugToId);
      return { elements: translated };
    }

    // 3+4+5: domyślny układ
    return buildDefaultLayout();
  }, [activeView?.formConfig, activeViewSlug, node.view?.formConfig, slugToId, buildDefaultLayout]);

  // ---------------------------------------------------------------------------
  // Strażnik niezapisanych zmian
  // ---------------------------------------------------------------------------

  // dirty = true przy pierwszej interakcji z builderem (onPointerDown na wrapperze).
  // Po zapisie (handleBuilderSave) → dirty = false.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const handleRequestClose = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  // Zamknięcie okienka „Nowy widok" (tło, Esc, X, „Anuluj") — porzuca też wpisaną nazwę i błąd.
  const closeNewViewModal = () => {
    setShowNewViewModal(false);
    setNewViewName('');
    setNewViewNameError('');
  };

  // ---------------------------------------------------------------------------
  // Dropdown layoutów — obsługa
  // ---------------------------------------------------------------------------

  // Lista layoutów to popover Radix (portal, Esc zamyka TYLKO listę — Radix obsługuje go w fazie
  // przechwytywania z `preventDefault`, więc okno kreatora się nie zamyka; klik poza listą i fokus
  // po zamknięciu też obsługuje Radix: po Esc/wyborze fokus wraca na przycisk listy, a gdy
  // zamknięcie otwiera inne okno („Nowy widok", pytanie o przełączenie) — fokus przejęty przez to
  // okno Radix uznaje za interakcję poza listą i fokusu mu nie odbiera).
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Po zamknięciu okienka „Nowy widok" fokus wraca na przycisk listy layoutów: przycisk, który
  // je otworzył („Utwórz nowy" w liście), znika razem z listą, więc rdzeń okien nie ma gdzie
  // oddać fokusu.
  const layoutTriggerRef = useRef<HTMLButtonElement>(null);
  const newViewWasOpenRef = useRef(false);
  useEffect(() => {
    if (showNewViewModal) {
      newViewWasOpenRef.current = true;
      return;
    }
    if (newViewWasOpenRef.current) {
      newViewWasOpenRef.current = false;
      layoutTriggerRef.current?.focus();
    }
  }, [showNewViewModal]);

  /**
   * Potwierdzenie przełączenia layoutu gdy są niezapisane zmiany w canvasie.
   * `{ slug }` przechowuje cel przełączenia; null = brak oczekującego przełączenia.
   * (slug może być null = layout domyślny, więc owijamy w obiekt żeby odróżnić od „brak").
   */
  const [pendingSwitch, setPendingSwitch] = useState<{ slug: string | null } | null>(null);

  /**
   * Faktyczne przełączenie aktywnego layoutu. Zmiana `activeViewSlug` wchodzi do klucza
   * re-mountu buildera (effectiveBuilderKey), więc canvas przeładowuje się świeżym seedem
   * wybranego layoutu. Czyścimy resetSeed i flagę dirty (świeży mount = czysto).
   */
  const performSelectView = (slug: string | null) => {
    setActiveViewSlug(slug);
    setResetSeed(null);
    setHasUnsavedChanges(false);
    setDropdownOpen(false);
  };

  /**
   * Przełącz aktywny widok. Gdy w canvasie są niezapisane zmiany — pytamy (przełączenie
   * przeładuje canvas i odrzuci te zmiany). Bez zmian — przełączamy od razu.
   */
  const handleSelectView = (slug: string | null) => {
    setDropdownOpen(false);
    if (slug === activeViewSlug) return;
    if (hasUnsavedChanges) {
      setPendingSwitch({ slug });
      return;
    }
    performSelectView(slug);
  };

  /** Zatwierdź tworzenie nowego widoku z modal. */
  const handleCreateView = () => {
    const name = newViewName.trim();
    if (!name) {
      setNewViewNameError(t('admin.templateMenuBuilder.formBuilder.newViewNameRequired'));
      return;
    }
    // Duplicate-name guard: case-insensitive, trimmed, scoped to this entity's form views.
    const nameLower = name.toLowerCase();
    const hasDuplicate = formViews.some((v) => v.name.trim().toLowerCase() === nameLower);
    if (hasDuplicate) {
      setNewViewNameError(
        t('admin.menuItemViews.duplicateName', {
          defaultValue: 'Widok o tej nazwie już istnieje.',
        }),
      );
      return;
    }
    if (!entitySlug) return;

    // Seed nowego widoku = pełny domyślny układ (analogia do nowego new_table)
    const defaultLayout = buildDefaultLayout();
    const rawElements = Array.isArray((defaultLayout as { elements?: unknown }).elements)
      ? (defaultLayout as { elements: BuilderElement[] }).elements
      : [];
    const seedFormConfig: ModuleTemplateFormConfig = {
      elements: translateElementsToFormConfig(rawElements, idToSlug),
    };

    const newSlug = onCreateView(entitySlug, name, seedFormConfig);
    setActiveViewSlug(newSlug);
    setShowNewViewModal(false);
    setNewViewName('');
    setNewViewNameError('');
    setDropdownOpen(false);
  };

  // ---------------------------------------------------------------------------
  // Zapis: layoutConfig (fieldId, drzewo) → formConfig (fieldSlug)
  // ---------------------------------------------------------------------------

  const handleBuilderSave = useCallback(
    async (layoutConfig: Record<string, unknown>) => {
      // readOnlyMode: podgląd domyślnego layoutu — zapis jest niedozwolony.
      // Przycisk Zapisz jest ukryty przez SystemModuleBuilderPage (readOnlyMode prop),
      // ale blokujemy też ścieżkę callbacku żeby klawiatura nie mogła obejść blokady.
      if (readOnlyMode) return;

      const rawElements = Array.isArray((layoutConfig as { elements?: unknown }).elements)
        ? (layoutConfig as { elements: BuilderElement[] }).elements
        : [];

      const translatedElements = translateElementsToFormConfig(rawElements, idToSlug);
      const formConfig: ModuleTemplateFormConfig = { elements: translatedElements };
      setHasUnsavedChanges(false);

      // Przekaż viewRef gdy pracujemy z nazwanym layoutem (nie domyślnym)
      const viewRef = activeViewSlug && entitySlug ? `${entitySlug}:${activeViewSlug}` : undefined;
      onSave(formConfig, viewRef);
    },
    [readOnlyMode, idToSlug, activeViewSlug, entitySlug, onSave],
  );

  // ---------------------------------------------------------------------------
  // Reset kontekstowy (tylko gdy aktywny layout NIE jest domyślny)
  // ---------------------------------------------------------------------------

  /**
   * Buduje seed dla „Wróć do defaultu" i „Zacznij od zera".
   * Zwraca Record<string,unknown> do przekazania jako initialLayoutConfig builderowi.
   * Implementacja deleguje do SystemModuleBuilderPage przez klucz re-mount.
   */
  const [resetKey, setResetKey] = useState(0);
  const [resetSeed, setResetSeed] = useState<Record<string, unknown> | null>(null);

  // Aktualny seed do przekazania jako initialLayoutConfig (normalny lub po resecie)
  const effectiveLayoutConfig = resetSeed ?? initialLayoutConfig;

  const handleResetToDefault = () => {
    setResetSeed(buildDefaultLayout());
    setResetKey((k) => k + 1);
    setHasUnsavedChanges(true);
  };

  const handleResetBlank = () => {
    setResetSeed({ elements: [] });
    setResetKey((k) => k + 1);
    setHasUnsavedChanges(true);
  };

  // Wyczyść resetSeed po wczytaniu (żeby kolejna iteracja nie duplikowała).
  // setResetSeed jest stabilny (setState z useState) — nie jest w deps.
  useEffect(() => {
    setResetSeed(null);
  }, [resetKey]);

  const title = t('admin.templateMenuBuilder.formBuilder.title');

  // Czy jesteśmy gotowi do renderowania buildera
  const isReady = !isReference || (!kartotekaQuery.isLoading && !kartotekaQuery.isError);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Przełącznik layoutu (dropdown nazwanych layoutów + reset kontekstowy) — wstrzykiwany
  // do paska buildera przez `embeddedLayoutSwitcher`, zamiast osobnego rzędu w nagłówku
  // modala (jeden rząd kontrolek, bez dublowania z wbudowanym LayoutSwitcher).
  const layoutSwitcherNode =
    entity && entitySlug ? (
      <div className="flex items-center gap-2">
        {/* Lista wyboru layoutu formularza: popover Radix (portal, Esc/klik poza/fokus po zamknięciu
            obsługuje Radix). `modal`: klik poza listą (także w ciemne tło kreatora) tylko ją
            zamyka — bez tego ten sam klik zamykałby też kreator (albo pytał o porzucenie zmian);
            lista w portalu zatrzymuje też Tab w sobie (poza panelem okna). */}
        <PopoverPrimitive.Root modal open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <PopoverPrimitive.Trigger asChild>
            <Button
              ref={layoutTriggerRef}
              variant="secondary"
              size="sm"
              className="h-auto gap-1.5 rounded-md border-border-primary bg-bg-secondary px-3 py-1.5 text-sm hover:bg-bg-tertiary"
              data-testid="form-builder-layout-dropdown-trigger"
            >
              <span className="max-w-[160px] truncate font-medium">
                {activeView
                  ? activeView.name
                  : t('admin.templateMenuBuilder.formBuilder.defaultLayoutName')}
              </span>
              {(activeView?.isDefault ?? !activeViewSlug) && (
                <span className="ml-1 rounded bg-accent-100 px-1 py-0.5 text-[10px] font-semibold text-accent-700">
                  {t('admin.templateMenuBuilder.formBuilder.defaultBadge')}
                </span>
              )}
              <svg
                className="h-3.5 w-3.5 text-text-muted"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Button>
          </PopoverPrimitive.Trigger>

          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="end"
              sideOffset={4}
              className="z-[10001] min-w-[220px] rounded-md border border-border-primary bg-bg-primary shadow-lg focus:outline-none"
              onCloseAutoFocus={(e) => {
                // Tryb modalny oddaje fokus przyciskowi listy po każdym zamknięciu. Gdy zamknięcie
                // otworzyło inne okno („Nowy widok", pytanie o przełączenie), fokus ma już ono —
                // nie odbieramy go (po Esc/wyborze/klik poza fokus jest na `<body>`: wtedy wraca).
                const active = document.activeElement;
                if (active && active !== document.body) e.preventDefault();
              }}
            >
              <div className="border-b border-border-primary p-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  {t('admin.templateMenuBuilder.formBuilder.layoutsHeader')}
                </p>
              </div>
              <div className="max-h-60 overflow-y-auto py-1">
                {/* Widok domyślny (wirtualny) */}
                <Button
                  variant="ghost"
                  onClick={() => handleSelectView(null)}
                  className={cn(
                    'h-auto w-full justify-start gap-2 rounded-none px-3 py-2 text-left text-sm font-normal',
                    !activeViewSlug
                      ? 'bg-accent-50 text-accent-700 hover:bg-accent-50'
                      : 'text-text-primary hover:bg-bg-secondary',
                  )}
                  data-testid="form-builder-layout-option-default"
                >
                  <span className="flex-1 truncate font-medium">
                    {t('admin.templateMenuBuilder.formBuilder.defaultLayoutName')}
                  </span>
                  <span className="rounded bg-accent-100 px-1 py-0.5 text-[10px] font-semibold text-accent-700">
                    {t('admin.templateMenuBuilder.formBuilder.defaultBadge')}
                  </span>
                </Button>
                {/* Nazwane widoki form */}
                {formViews
                  .filter((v) => !v.isDefault)
                  .map((v) => (
                    <Button
                      key={v.slug}
                      variant="ghost"
                      onClick={() => handleSelectView(v.slug)}
                      className={cn(
                        'h-auto w-full justify-start rounded-none px-3 py-2 text-left text-sm font-normal',
                        activeViewSlug === v.slug
                          ? 'bg-accent-50 text-accent-700 hover:bg-accent-50'
                          : 'text-text-primary hover:bg-bg-secondary',
                      )}
                      data-testid={`form-builder-layout-option-${v.slug}`}
                    >
                      <span className="flex-1 truncate font-medium">{v.name}</span>
                    </Button>
                  ))}
              </div>
              {!readOnlyMode && (
                <div className="border-t border-border-primary p-1">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      // Najpierw zamknięcie listy w osobnym zatwierdzeniu: pułapka fokusu
                      // popovera (tryb modalny) działa do jego odmontowania i odebrałaby fokus
                      // polu nazwy okna, które montuje się w tym samym zatwierdzeniu.
                      flushSync(() => setDropdownOpen(false));
                      setShowNewViewModal(true);
                    }}
                    className="h-auto w-full justify-start gap-2 rounded px-3 py-2 text-left text-sm font-medium text-accent-600 hover:bg-accent-50"
                    data-testid="form-builder-layout-create"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {t('admin.templateMenuBuilder.formBuilder.createNew')}
                  </Button>
                </div>
              )}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>

        {/* Reset kontekstowy — tylko gdy aktywny layout NIE jest domyślny i nie jest read-only */}
        {!isActiveDefault && !readOnlyMode && (
          <>
            <button
              type="button"
              onClick={handleResetToDefault}
              className="rounded px-2.5 py-1.5 text-xs text-text-secondary hover:bg-bg-secondary"
              data-testid="form-builder-reset-to-default"
            >
              {t('tenant.systemModuleBuilder.resetToDefault')}
            </button>
            <button
              type="button"
              onClick={handleResetBlank}
              className="rounded px-2.5 py-1.5 text-xs text-text-secondary hover:bg-bg-secondary"
              data-testid="form-builder-reset-blank"
            >
              {t('tenant.systemModuleBuilder.startBlank')}
            </button>
          </>
        )}
      </div>
    ) : null;

  return (
    <>
      {/* Tytuł + zamknięcie. Przełącznik layoutu żyje w pasku buildera (embeddedLayoutSwitcher),
          nie w nagłówku — jeden rząd kontrolek bez dublowania. */}
      <BuilderOverlay
        testId="menu-item-form-builder-modal"
        onRequestClose={handleRequestClose}
        width="full"
        title={title}
        subtitle={node.moduleCode ? (node.label ?? node.moduleCode) : undefined}
        banner={
          readOnlyMode ? (
            <span
              className="rounded-md bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
              data-testid="form-builder-read-only-banner"
            >
              {t('admin.menuItemViews.readOnlyBanner', {
                defaultValue: 'Podgląd — tylko do odczytu, nie można edytować',
              })}
            </span>
          ) : undefined
        }
        closeLabel={t('admin.templateMenuBuilder.formBuilder.close')}
        closeTestId="menu-item-form-builder-close"
      >
        {!entity || !entitySlug ? (
          <p className="text-sm text-text-muted">
            {t('admin.templateMenuBuilder.formBuilder.entityNotFound')}
          </p>
        ) : isReference && kartotekaQuery.isLoading ? (
          <div className="flex items-center justify-center p-8">
            <span className="text-sm text-text-muted">
              {t('admin.templateMenuBuilder.formBuilder.loading')}
            </span>
          </div>
        ) : isReference && kartotekaQuery.isError ? (
          <p className="text-sm text-red-600 dark:text-red-400">
            {t('admin.templateMenuBuilder.formBuilder.loadError')}
          </p>
        ) : isReady ? (
          // Klucz zawiera activeViewSlug + resetKey: przełączenie layoutu LUB reset
          // re-mountuje builder, więc canvas przeładowuje świeży seed wybranego layoutu
          // (bez tego switch nie odświeżał canvasu — pokazywał stary/pusty układ).
          // „Niezapisane zmiany" bierzemy z realnego stanu buildera (onDirtyChange),
          // nie z kliknięcia — sam klik (np. w dropdown) nie zapala strażnika zamknięcia.
          <div
            key={`${activeViewSlug ?? '__default__'}-${resetKey}`}
            className="w-full overflow-hidden rounded-lg border border-border-secondary"
          >
            <SystemModuleBuilderPage
              embeddedMode
              slug={isReference ? kartotekaSlug : entitySlug}
              initialLayoutConfig={effectiveLayoutConfig}
              onSaveKartotekaLayout={handleBuilderSave}
              onDirtyChange={setHasUnsavedChanges}
              embeddedLayoutSwitcher={layoutSwitcherNode}
              externalFields={externalFields}
              externalHelperFields={isReference ? kartotekaQuery.data?.helperFields : undefined}
              externalHelperLayouts={
                isReference ? kartotekaQuery.data?.helperDefaultLayouts : undefined
              }
              readOnlyTabTypes={FORM_READ_ONLY_TAB_TYPES}
              allowTabManagement
              readOnlyTabCollections={FORM_READ_ONLY_COLLECTIONS}
              readOnlyMode={readOnlyMode}
            />
          </div>
        ) : null}
      </BuilderOverlay>

      {/* Okienko tworzenia nowego widoku formularza — leży nad kreatorem (kolejność na stosie
          okien, bez z-index); Esc i tło zamykają tylko je. */}
      <Modal
        open={showNewViewModal}
        onClose={closeNewViewModal}
        size="sm"
        title={t('admin.templateMenuBuilder.formBuilder.newViewTitle')}
        testId="form-builder-new-view-modal"
        footer={
          <>
            <Button variant="secondary" onClick={closeNewViewModal}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateView} data-testid="form-builder-new-view-confirm">
              {t('admin.templateMenuBuilder.formBuilder.newViewConfirm')}
            </Button>
          </>
        }
      >
        <Input
          label={t('admin.templateMenuBuilder.formBuilder.newViewNameLabel')}
          value={newViewName}
          onChange={(e) => {
            setNewViewName(e.target.value);
            if (newViewNameError) setNewViewNameError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreateView();
          }}
          autoFocus
          error={newViewNameError || undefined}
          placeholder={t('admin.templateMenuBuilder.formBuilder.newViewNamePlaceholder')}
          data-testid="form-builder-new-view-name-input"
        />
      </Modal>

      <ConfirmDialog
        open={showCloseConfirm}
        variant="danger"
        title={t('admin.templateMenuBuilder.formBuilder.confirmClose.title')}
        message={t('admin.templateMenuBuilder.formBuilder.confirmClose.message')}
        confirmLabel={t('admin.templateMenuBuilder.formBuilder.confirmClose.confirm')}
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
        onClose={() => setShowCloseConfirm(false)}
      />

      <ConfirmDialog
        open={pendingSwitch !== null}
        variant="danger"
        title={t('admin.templateMenuBuilder.formBuilder.confirmSwitch.title')}
        message={t('admin.templateMenuBuilder.formBuilder.confirmSwitch.message')}
        confirmLabel={t('admin.templateMenuBuilder.formBuilder.confirmSwitch.confirm')}
        onConfirm={() => {
          if (pendingSwitch) performSelectView(pendingSwitch.slug);
          setPendingSwitch(null);
        }}
        onClose={() => setPendingSwitch(null)}
      />
    </>
  );
}
